import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './table';
import { SortableTableHead } from './sortable-table-head';
import { useTableSort } from '../../hooks/useTableSort';

export interface DataTableColumn<T> {
    /** Unique column id, used as the sort field */
    key: string;
    header: React.ReactNode;
    cell: (item: T) => React.ReactNode;
    /**
     * Value to sort by. Providing this makes the column sortable —
     * numbers sort numerically, everything else alphabetically.
     */
    sortValue?: (item: T) => string | number | null | undefined;
    /** Class applied to body cells in this column */
    className?: string;
    /** Class applied to the header cell */
    headClassName?: string;
}

interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    items: T[];
    rowKey: (item: T) => string;
    onRowClick?: (item: T) => void;
    /** Rows per page. Omit to render all items without pagination. */
    pageSize?: number;
}

export function DataTable<T>({
    columns,
    items,
    rowKey,
    onRowClick,
    pageSize,
}: DataTableProps<T>) {
    const { t } = useTranslation();
    const { sortField, sortDirection, toggleSort } = useTableSort<string>();
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
    }, [items, sortField, sortDirection]);

    const sortedItems = useMemo(() => {
        const column = columns.find((c) => c.key === sortField);
        if (!column?.sortValue) return items;
        const direction = sortDirection === 'desc' ? -1 : 1;
        const getValue = column.sortValue;
        return [...items].sort((a, b) => {
            const aValue = getValue(a);
            const bValue = getValue(b);
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction * (aValue - bValue);
            }
            return (
                direction *
                String(aValue ?? '').localeCompare(String(bValue ?? ''))
            );
        });
    }, [items, columns, sortField, sortDirection]);

    const totalPages = pageSize
        ? Math.max(1, Math.ceil(sortedItems.length / pageSize))
        : 1;
    // Clamp so a shrinking item set never leaves the page out of range
    // before the reset effect runs
    const currentPage = Math.min(page, totalPages);
    const visibleItems = pageSize
        ? sortedItems.slice(
              (currentPage - 1) * pageSize,
              currentPage * pageSize
          )
        : sortedItems;
    const showPagination = pageSize != null && sortedItems.length > pageSize;
    const rangeStart = (currentPage - 1) * (pageSize ?? 0) + 1;
    const rangeEnd = Math.min(
        currentPage * (pageSize ?? 0),
        sortedItems.length
    );

    return (
        <>
            {/* Fixed layout distributes width evenly across columns (except those
                with an explicit width via headClassName) instead of sizing by content */}
            <Table className="table-fixed">
            <TableHeader>
                <TableRow>
                    {columns.map((column) =>
                        column.sortValue ? (
                            <SortableTableHead
                                key={column.key}
                                field={column.key}
                                label={column.header}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onToggle={toggleSort}
                                className={column.headClassName}
                            />
                        ) : (
                            <TableHead
                                key={column.key}
                                className={column.headClassName}
                            >
                                {column.header}
                            </TableHead>
                        )
                    )}
                </TableRow>
            </TableHeader>
            <TableBody>
                {visibleItems.map((item) => (
                    <TableRow
                        key={rowKey(item)}
                        className={onRowClick ? 'cursor-pointer' : undefined}
                        onClick={
                            onRowClick ? () => onRowClick(item) : undefined
                        }
                    >
                        {columns.map((column) => (
                            <TableCell
                                key={column.key}
                                className={cn(
                                    // Let long content wrap so columns shrink
                                    // to the table width instead of overflowing
                                    // into horizontal scroll
                                    '[overflow-wrap:anywhere]',
                                    column.className
                                )}
                            >
                                {column.cell(item)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
        {showPagination && (
            <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                    {t('dataTable.pagination.showing', {
                        start: rangeStart,
                        end: rangeEnd,
                        total: sortedItems.length,
                    })}
                </p>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                        {t('dataTable.pagination.page', {
                            page: currentPage,
                            totalPages,
                        })}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={currentPage <= 1}
                            onClick={() => setPage(currentPage - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                            {t('dataTable.pagination.previous')}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={currentPage >= totalPages}
                            onClick={() => setPage(currentPage + 1)}
                        >
                            {t('dataTable.pagination.next')}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
